import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Settings, ArrowLeft, DollarSign, Globe, Plus, Trash2, RefreshCw, Loader2, Check, Languages, Brain, Target, Lightbulb, X, Mail, Send, CreditCard, Building2, Banknote, FileText, Wallet, MoreHorizontal, Star, Pencil, Power
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { brandName } = useBrand();
  const { language, setLanguage, availableLanguages, t } = useLanguage();
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  
  // Form state
  const [defaultCurrency, setDefaultCurrency] = useState("USD");
  const [preferredCurrency, setPreferredCurrency] = useState("");
  const [enabledCurrencies, setEnabledCurrencies] = useState(["USD"]);
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  
  // Add currency dialog
  const [showAddCurrency, setShowAddCurrency] = useState(false);
  const [newCurrency, setNewCurrency] = useState({ code: "", name: "", symbol: "" });
  const [isAddingCurrency, setIsAddingCurrency] = useState(false);

  // AI Memory state
  const [memories, setMemories] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [isLoadingAIMemory, setIsLoadingAIMemory] = useState(false);
  const [showAddMemory, setShowAddMemory] = useState(false);
  const [showAddObjective, setShowAddObjective] = useState(false);
  
  // SMTP Settings state (for company users)
  const [smtpEnabled, setSmtpEnabled] = useState(false);
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("587");
  const [smtpUsername, setSmtpUsername] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpUseTls, setSmtpUseTls] = useState(true);
  const [smtpFromName, setSmtpFromName] = useState("");
  const [smtpFromEmail, setSmtpFromEmail] = useState("");
  const [isTestingSmtp, setIsTestingSmtp] = useState(false);
  const [newMemory, setNewMemory] = useState("");
  const [newObjective, setNewObjective] = useState({ content: "", target_date: "", target_amount: "" });
  const [isAddingMemory, setIsAddingMemory] = useState(false);
  const [isAddingObjective, setIsAddingObjective] = useState(false);

  // Company Profile state (for company users)
  const [companyAddress, setCompanyAddress] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyLogoUrl, setCompanyLogoUrl] = useState("");
  const [isSavingCompanyProfile, setIsSavingCompanyProfile] = useState(false);

  // Payment Methods state (for company users)
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const [showPaymentMethodDialog, setShowPaymentMethodDialog] = useState(false);
  const [editingPaymentMethod, setEditingPaymentMethod] = useState(null);
  const [paymentMethodForm, setPaymentMethodForm] = useState({
    name: "",
    type: "other",
    bank_name: "",
    account_name: "",
    account_number_last4: "",
    gateway_type: "",
    icon: "credit-card",
    color: "gray",
    notes: ""
  });
  const [isSavingPaymentMethod, setIsSavingPaymentMethod] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync selectedLanguage with context language
  useEffect(() => {
    setSelectedLanguage(language);
  }, [language]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [settingsRes, currenciesRes] = await Promise.all([
        api.get("/settings"),
        api.get("/currencies")
      ]);
      
      setSettings(settingsRes.data);
      setCurrencies(currenciesRes.data);
      
      setDefaultCurrency(settingsRes.data.default_currency || "USD");
      setPreferredCurrency(settingsRes.data.preferred_currency || "");
      setEnabledCurrencies(settingsRes.data.enabled_currencies || ["USD"]);
      
      // Load SMTP settings
      setSmtpEnabled(settingsRes.data.smtp_enabled || false);
      setSmtpHost(settingsRes.data.smtp_host || "");
      setSmtpPort(settingsRes.data.smtp_port?.toString() || "587");
      setSmtpUsername(settingsRes.data.smtp_username || "");
      setSmtpUseTls(settingsRes.data.smtp_use_tls ?? true);
      setSmtpFromName(settingsRes.data.smtp_from_name || "");
      setSmtpFromEmail(settingsRes.data.smtp_from_email || "");
      
      // Load company profile from user data
      if (user?.user_type === "company") {
        setCompanyAddress(user.company_address || "");
        setCompanyPhone(user.company_phone || "");
        setCompanyEmail(user.company_email || "");
        setCompanyLogoUrl(user.company_logo_url || "");
      }
      
      // Only fetch exchange rates if a preferred currency is set
      if (settingsRes.data.preferred_currency) {
        fetchExchangeRates(settingsRes.data.preferred_currency);
      }
      
      // Fetch AI memory data
      fetchAIMemory();
      
      // Fetch payment methods for company users
      if (settingsRes.data && user?.user_type === "company") {
        fetchPaymentMethods();
      }
    } catch (error) {
      toast.error(t("failed_to_load", "errors"));
    } finally {
      setIsLoading(false);
    }
  };

  // Payment Methods Functions
  const fetchPaymentMethods = async () => {
    setIsLoadingPaymentMethods(true);
    try {
      const response = await api.get("/company-settings");
      setPaymentMethods(response.data.payment_methods || []);
    } catch (error) {
      console.error("Failed to fetch payment methods:", error);
    } finally {
      setIsLoadingPaymentMethods(false);
    }
  };

  const openAddPaymentMethod = () => {
    setEditingPaymentMethod(null);
    setPaymentMethodForm({
      name: "",
      type: "other",
      bank_name: "",
      account_name: "",
      account_number_last4: "",
      gateway_type: "",
      icon: "credit-card",
      color: "gray",
      notes: ""
    });
    setShowPaymentMethodDialog(true);
  };

  const openEditPaymentMethod = (method) => {
    setEditingPaymentMethod(method);
    setPaymentMethodForm({
      name: method.name || "",
      type: method.type || "other",
      bank_name: method.bank_name || "",
      account_name: method.account_name || "",
      account_number_last4: method.account_number_last4 || "",
      gateway_type: method.gateway_type || "",
      icon: method.icon || "credit-card",
      color: method.color || "gray",
      notes: method.notes || ""
    });
    setShowPaymentMethodDialog(true);
  };

  const handleSavePaymentMethod = async () => {
    if (!paymentMethodForm.name.trim()) {
      toast.error("Please enter a name for the payment method");
      return;
    }
    
    setIsSavingPaymentMethod(true);
    try {
      if (editingPaymentMethod) {
        // Update existing
        const response = await api.put(`/company-settings/payment-methods/${editingPaymentMethod.id}`, paymentMethodForm);
        setPaymentMethods(paymentMethods.map(pm => 
          pm.id === editingPaymentMethod.id ? response.data.payment_method : pm
        ));
        toast.success("Payment method updated");
      } else {
        // Create new
        const response = await api.post("/company-settings/payment-methods", paymentMethodForm);
        setPaymentMethods([...paymentMethods, response.data.payment_method]);
        toast.success("Payment method added");
      }
      setShowPaymentMethodDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save payment method");
    } finally {
      setIsSavingPaymentMethod(false);
    }
  };

  const handleDeletePaymentMethod = async (methodId) => {
    try {
      await api.delete(`/company-settings/payment-methods/${methodId}`);
      setPaymentMethods(paymentMethods.filter(pm => pm.id !== methodId));
      toast.success("Payment method removed");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete payment method");
    }
  };

  const handleTogglePaymentMethod = async (methodId) => {
    try {
      const response = await api.patch(`/company-settings/payment-methods/${methodId}/toggle`);
      setPaymentMethods(paymentMethods.map(pm => 
        pm.id === methodId ? { ...pm, enabled: response.data.enabled } : pm
      ));
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to toggle payment method");
    }
  };

  const handleSetDefaultPaymentMethod = async (methodId) => {
    try {
      await api.patch(`/company-settings/payment-methods/${methodId}/set-default`);
      setPaymentMethods(paymentMethods.map(pm => ({
        ...pm,
        is_default: pm.id === methodId
      })));
      toast.success("Default payment method updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set default");
    }
  };

  const getPaymentMethodIcon = (type, gatewayType) => {
    if (type === "gateway") {
      if (gatewayType === "stripe") return <CreditCard className="h-4 w-4" />;
      if (gatewayType === "paypal") return <Wallet className="h-4 w-4" />;
      return <CreditCard className="h-4 w-4" />;
    }
    if (type === "bank") return <Building2 className="h-4 w-4" />;
    if (type === "cash") return <Banknote className="h-4 w-4" />;
    if (type === "check") return <FileText className="h-4 w-4" />;
    return <CreditCard className="h-4 w-4" />;
  };

  const getPaymentMethodTypeBadge = (type) => {
    const colors = {
      gateway: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
      bank: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
      cash: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
      check: "bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-300",
      other: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300"
    };
    return colors[type] || colors.other;
  };

  const fetchExchangeRates = async (baseCurrency) => {
    setIsLoadingRates(true);
    try {
      const targets = currencies.map(c => c.code).filter(c => c !== baseCurrency).join(",") || "USD,ARS,INR,EUR,GBP";
      const response = await api.get(`/exchange-rates?base=${baseCurrency}&targets=${targets}`);
      setExchangeRates(response.data.rates || []);
    } catch (error) {
      console.error("Failed to fetch exchange rates");
    } finally {
      setIsLoadingRates(false);
    }
  };

  // SMTP Settings Functions
  const saveSmtpSettings = async () => {
    setIsSaving(true);
    try {
      await api.put("/settings", {
        smtp_enabled: smtpEnabled,
        smtp_host: smtpHost,
        smtp_port: parseInt(smtpPort) || 587,
        smtp_username: smtpUsername,
        smtp_password: smtpPassword || undefined,
        smtp_use_tls: smtpUseTls,
        smtp_from_name: smtpFromName,
        smtp_from_email: smtpFromEmail
      });
      toast.success("SMTP settings saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save SMTP settings");
    } finally {
      setIsSaving(false);
    }
  };

  // Company Profile Functions
  const saveCompanyProfile = async () => {
    setIsSavingCompanyProfile(true);
    try {
      await api.put("/auth/company-profile", {
        company_address: companyAddress,
        company_phone: companyPhone,
        company_email: companyEmail,
        company_logo_url: companyLogoUrl
      });
      toast.success(t("company_profile_saved", "settings") || "Company profile saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save company profile");
    } finally {
      setIsSavingCompanyProfile(false);
    }
  };

  const testSmtpConnection = async () => {
    if (!smtpHost || !smtpUsername) {
      toast.error("Please fill in SMTP host and username first");
      return;
    }
    
    setIsTestingSmtp(true);
    try {
      // Save settings first
      await api.put("/settings", {
        smtp_enabled: true,
        smtp_host: smtpHost,
        smtp_port: parseInt(smtpPort) || 587,
        smtp_username: smtpUsername,
        smtp_password: smtpPassword || undefined,
        smtp_use_tls: smtpUseTls,
        smtp_from_name: smtpFromName,
        smtp_from_email: smtpFromEmail
      });
      
      // Then test
      const response = await api.post("/settings/smtp/test");
      toast.success(response.data.message || "Test email sent successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "SMTP test failed");
    } finally {
      setIsTestingSmtp(false);
    }
  };

  // AI Memory Functions
  const fetchAIMemory = async () => {
    setIsLoadingAIMemory(true);
    try {
      const [memoriesRes, objectivesRes] = await Promise.all([
        api.get("/kb/memories"),
        api.get("/kb/objectives")
      ]);
      setMemories(memoriesRes.data || []);
      setObjectives(objectivesRes.data || []);
    } catch (error) {
      console.error("Failed to fetch AI memory:", error);
    } finally {
      setIsLoadingAIMemory(false);
    }
  };

  const handleAddMemory = async () => {
    if (!newMemory.trim()) {
      toast.error("Please enter a memory");
      return;
    }
    setIsAddingMemory(true);
    try {
      const response = await api.post("/kb/memories", { content: newMemory.trim() });
      setMemories([...memories, response.data]);
      setNewMemory("");
      setShowAddMemory(false);
      toast.success("Memory added successfully");
    } catch (error) {
      toast.error("Failed to add memory");
    } finally {
      setIsAddingMemory(false);
    }
  };

  const handleDeleteMemory = async (memoryId) => {
    try {
      await api.delete(`/kb/memories/${memoryId}`);
      setMemories(memories.filter(m => m.id !== memoryId));
      toast.success("Memory deleted");
    } catch (error) {
      toast.error("Failed to delete memory");
    }
  };

  const handleAddObjective = async () => {
    if (!newObjective.content.trim()) {
      toast.error("Please enter an objective");
      return;
    }
    setIsAddingObjective(true);
    try {
      const objectiveData = {
        content: newObjective.content.trim(),
        metadata: {}
      };
      if (newObjective.target_date) {
        objectiveData.metadata.target_date = newObjective.target_date;
      }
      if (newObjective.target_amount) {
        objectiveData.metadata.target_amount = parseFloat(newObjective.target_amount);
      }
      
      const response = await api.post("/kb/objectives", objectiveData);
      setObjectives([...objectives, response.data]);
      setNewObjective({ content: "", target_date: "", target_amount: "" });
      setShowAddObjective(false);
      toast.success("Objective added successfully");
    } catch (error) {
      toast.error("Failed to add objective");
    } finally {
      setIsAddingObjective(false);
    }
  };

  const handleDeleteObjective = async (objectiveId) => {
    try {
      await api.delete(`/kb/objectives/${objectiveId}`);
      setObjectives(objectives.filter(o => o.id !== objectiveId));
      toast.success("Objective deleted");
    } catch (error) {
      toast.error("Failed to delete objective");
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      // Ensure default and preferred currencies are in enabled list
      let updatedEnabled = [...enabledCurrencies];
      if (!updatedEnabled.includes(defaultCurrency)) {
        updatedEnabled.push(defaultCurrency);
      }
      if (preferredCurrency && !updatedEnabled.includes(preferredCurrency)) {
        updatedEnabled.push(preferredCurrency);
      }
      
      await api.put("/settings", {
        default_currency: defaultCurrency,
        preferred_currency: preferredCurrency || null,
        enabled_currencies: updatedEnabled,
        language: selectedLanguage
      });
      
      setEnabledCurrencies(updatedEnabled);
      
      // Update language context if changed
      if (selectedLanguage !== language) {
        setLanguage(selectedLanguage);
      }
      
      toast.success(t("settings_saved", "settings"));
      // Only fetch rates if preferred currency is set
      if (preferredCurrency) {
        fetchExchangeRates(preferredCurrency);
      } else {
        setExchangeRates([]);  // Clear rates when no preferred currency
      }
    } catch (error) {
      toast.error(t("failed_to_save", "errors"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddCurrency = async () => {
    if (!newCurrency.code || !newCurrency.name || !newCurrency.symbol) {
      toast.error(t("required", "common"));
      return;
    }
    
    setIsAddingCurrency(true);
    try {
      const response = await api.post("/currencies", {
        code: newCurrency.code.toUpperCase(),
        name: newCurrency.name,
        symbol: newCurrency.symbol,
        decimal_places: 2
      });
      
      setCurrencies([...currencies, response.data]);
      if (!enabledCurrencies.includes(response.data.code)) {
        setEnabledCurrencies([...enabledCurrencies, response.data.code]);
      }
      
      setNewCurrency({ code: "", name: "", symbol: "" });
      setShowAddCurrency(false);
      toast.success(t("currency_added", "settings"));
    } catch (error) {
      toast.error(t("failed_to_save", "errors"));
    } finally {
      setIsAddingCurrency(false);
    }
  };

  const toggleCurrencyEnabled = (code) => {
    if (code === defaultCurrency) {
      toast.error(t("cannot_disable_default", "settings"));
      return;
    }
    if (code === preferredCurrency) {
      toast.error(t("cannot_disable_preferred", "settings"));
      return;
    }
    
    if (enabledCurrencies.includes(code)) {
      setEnabledCurrencies(enabledCurrencies.filter(c => c !== code));
    } else {
      setEnabledCurrencies([...enabledCurrencies, code]);
    }
  };

  const getCurrencySymbol = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency?.symbol || code;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Settings className="h-6 w-6 text-[hsl(var(--ff-primary))]" />
            <span className="font-semibold text-lg">{t("settings", "settings")}</span>
          </div>
          <Button onClick={handleSaveSettings} disabled={isSaving} data-testid="save-settings-button">
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
            {t("save_settings", "settings")}
          </Button>
        </div>
      </header>

      <main className="container max-w-4xl mx-auto p-6 space-y-6">
        {/* Currency Settings */}
        <Card data-testid="currency-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              {t("currency_settings", "settings")}
            </CardTitle>
            <CardDescription>
              {t("configure_currencies", "settings")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Default & Preferred Currency */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>{t("default_currency", "settings")}</Label>
                <Select value={defaultCurrency} onValueChange={setDefaultCurrency}>
                  <SelectTrigger data-testid="default-currency-select">
                    <SelectValue placeholder={t("select_default_currency", "settings")} />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.symbol} {c.name} ({c.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t("default_currency_desc", "settings")}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>{t("preferred_currency", "settings")}</Label>
                <Select value={preferredCurrency || "none"} onValueChange={(v) => setPreferredCurrency(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="preferred-currency-select">
                    <SelectValue placeholder={t("select_preferred_currency", "settings")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("none_use_default", "settings")}</SelectItem>
                    {currencies.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.symbol} {c.name} ({c.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t("preferred_currency_desc", "settings")}
                </p>
              </div>
            </div>

            {/* Enabled Currencies */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>{t("enabled_currencies", "settings")}</Label>
                <Button variant="outline" size="sm" onClick={() => setShowAddCurrency(true)} data-testid="add-currency-button">
                  <Plus className="h-4 w-4 mr-1" /> {t("add_currency", "settings")}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("enabled_currencies_desc", "settings")}
              </p>
              <div className="flex flex-wrap gap-2">
                {currencies.map((c) => {
                  const isEnabled = enabledCurrencies.includes(c.code);
                  const isDefault = c.code === defaultCurrency;
                  const isPreferred = c.code === preferredCurrency;
                  
                  return (
                    <Badge
                      key={c.code}
                      variant={isEnabled ? "default" : "outline"}
                      className={`cursor-pointer transition-all ${
                        isEnabled 
                          ? "bg-[hsl(var(--ff-primary))] hover:bg-[hsl(var(--ff-primary)/.8)]" 
                          : "hover:bg-muted"
                      } ${isDefault ? "ring-2 ring-[hsl(var(--ff-success))]" : ""} ${isPreferred ? "ring-2 ring-[hsl(var(--ff-info))]" : ""}`}
                      onClick={() => toggleCurrencyEnabled(c.code)}
                      data-testid={`currency-toggle-${c.code}`}
                    >
                      {c.symbol} {c.code}
                      {isDefault && <span className="ml-1 text-xs">({t("default", "common")})</span>}
                      {isPreferred && <span className="ml-1 text-xs">({t("preferred", "settings")})</span>}
                    </Badge>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Language Settings */}
        <Card data-testid="language-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Languages className="h-5 w-5" />
              {t("language_settings", "settings")}
            </CardTitle>
            <CardDescription>
              {t("language_settings_desc", "settings")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label>{t("language", "settings")}</Label>
              <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                <SelectTrigger className="w-full md:w-[300px]" data-testid="language-select">
                  <SelectValue placeholder={t("select_language", "settings")} />
                </SelectTrigger>
                <SelectContent>
                  {availableLanguages.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.native_name} ({lang.name})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {t("language_display_desc", "settings")}
              </p>
            </div>
          </CardContent>
        </Card>


        {/* Company Profile - Company Users Only */}
        {user?.user_type === "company" && (
          <Card data-testid="company-profile-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
                {t("company_profile", "settings") || "Company Profile"}
              </CardTitle>
              <CardDescription>
                {t("company_profile_desc", "settings") || "Configure your company information for invoices and templates"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t("company_address", "settings") || "Company Address"}</Label>
                <Textarea
                  placeholder="123 Business Street, City, State 12345"
                  value={companyAddress}
                  onChange={(e) => setCompanyAddress(e.target.value)}
                  data-testid="company-address-input"
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  {t("company_address_desc", "settings") || "This address will appear on your invoices"}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("company_phone", "settings") || "Company Phone"}</Label>
                  <Input
                    placeholder="+1 (555) 123-4567"
                    value={companyPhone}
                    onChange={(e) => setCompanyPhone(e.target.value)}
                    data-testid="company-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("company_email", "settings") || "Company Email"}</Label>
                  <Input
                    type="email"
                    placeholder="info@yourcompany.com"
                    value={companyEmail}
                    onChange={(e) => setCompanyEmail(e.target.value)}
                    data-testid="company-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("company_logo_url", "settings") || "Company Logo URL"}</Label>
                <Input
                  type="url"
                  placeholder="https://example.com/logo.png"
                  value={companyLogoUrl}
                  onChange={(e) => setCompanyLogoUrl(e.target.value)}
                  data-testid="company-logo-url-input"
                />
                <p className="text-xs text-muted-foreground">
                  {t("company_logo_url_desc", "settings") || "Optional: URL to your company logo image"}
                </p>
              </div>

              <div className="pt-2">
                <Button 
                  onClick={saveCompanyProfile} 
                  disabled={isSavingCompanyProfile}
                  data-testid="save-company-profile-button"
                >
                  {isSavingCompanyProfile ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> {t("saving", "common") || "Saving..."}</>
                  ) : (
                    <><Check className="h-4 w-4 mr-2" /> {t("save_company_profile", "settings") || "Save Company Profile"}</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* SMTP Settings - Company Users Only */}
        {user?.user_type === "company" && (
          <Card data-testid="smtp-settings-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
                Email Settings (SMTP)
              </CardTitle>
              <CardDescription>
                Configure your email server to send invoices directly to clients
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <Label className="font-medium">Enable Email Sending</Label>
                  <p className="text-xs text-muted-foreground">Allow sending invoices via email</p>
                </div>
                <Switch
                  checked={smtpEnabled}
                  onCheckedChange={setSmtpEnabled}
                  data-testid="smtp-enabled-switch"
                />
              </div>

              {smtpEnabled && (
                <div className="space-y-4 pt-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>SMTP Host *</Label>
                      <Input
                        placeholder="smtp.gmail.com"
                        value={smtpHost}
                        onChange={(e) => setSmtpHost(e.target.value)}
                        data-testid="smtp-host-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Port</Label>
                      <Input
                        type="number"
                        placeholder="587"
                        value={smtpPort}
                        onChange={(e) => setSmtpPort(e.target.value)}
                        data-testid="smtp-port-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Username *</Label>
                      <Input
                        placeholder="your-email@gmail.com"
                        value={smtpUsername}
                        onChange={(e) => setSmtpUsername(e.target.value)}
                        data-testid="smtp-username-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password / App Password *</Label>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        value={smtpPassword}
                        onChange={(e) => setSmtpPassword(e.target.value)}
                        data-testid="smtp-password-input"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div>
                      <Label>Use TLS/STARTTLS</Label>
                      <p className="text-xs text-muted-foreground">Recommended for security</p>
                    </div>
                    <Switch
                      checked={smtpUseTls}
                      onCheckedChange={setSmtpUseTls}
                      data-testid="smtp-tls-switch"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>From Name</Label>
                      <Input
                        placeholder="Your Company Name"
                        value={smtpFromName}
                        onChange={(e) => setSmtpFromName(e.target.value)}
                        data-testid="smtp-from-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>From Email</Label>
                      <Input
                        placeholder="invoices@yourcompany.com"
                        value={smtpFromEmail}
                        onChange={(e) => setSmtpFromEmail(e.target.value)}
                        data-testid="smtp-from-email-input"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button onClick={saveSmtpSettings} disabled={isSaving} data-testid="save-smtp-button">
                      {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                      Save Settings
                    </Button>
                    <Button variant="outline" onClick={testSmtpConnection} disabled={isTestingSmtp} data-testid="test-smtp-button">
                      {isTestingSmtp ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                      Send Test Email
                    </Button>
                  </div>

                  <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg text-sm">
                    <p className="font-medium text-blue-700 dark:text-blue-400 mb-1">Gmail Users:</p>
                    <p className="text-blue-600 dark:text-blue-300 text-xs">
                      Use smtp.gmail.com:587 with an App Password (not your regular password). 
                      Generate one at Google Account → Security → App Passwords.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payment Methods Settings - Company Users Only */}
        {user?.user_type === "company" && (
          <Card data-testid="payment-methods-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
                {t("payment_methods", "company") || "Payment Methods"}
              </CardTitle>
              <CardDescription>
                {t("payment_methods_desc", "company") || "Configure payment methods available for sales and recurring revenue"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingPaymentMethods ? (
                <div className="space-y-3">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Add Payment Method Button */}
                  <div className="flex justify-end">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={openAddPaymentMethod}
                      data-testid="add-payment-method-button"
                    >
                      <Plus className="h-4 w-4 mr-1" /> {t("add_payment_method", "company") || "Add Payment Method"}
                    </Button>
                  </div>

                  {/* Payment Methods List */}
                  {paymentMethods.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
                      <CreditCard className="h-10 w-10 mx-auto mb-3 opacity-50" />
                      <p className="text-sm font-medium">{t("no_payment_methods", "company") || "No payment methods configured"}</p>
                      <p className="text-xs mt-1">{t("add_first_payment_method", "company") || "Add payment methods to track revenue by payment channel"}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Group by type */}
                      {["gateway", "bank", "cash", "check", "other"].map(type => {
                        const methodsOfType = paymentMethods.filter(pm => pm.type === type);
                        if (methodsOfType.length === 0) return null;
                        
                        const typeLabels = {
                          gateway: t("payment_gateways", "company") || "Payment Gateways",
                          bank: t("bank_accounts", "company") || "Bank Accounts",
                          cash: t("cash", "company") || "Cash",
                          check: t("check", "company") || "Check",
                          other: t("other", "common") || "Other"
                        };
                        
                        return (
                          <div key={type} className="space-y-2">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide pt-2">
                              {typeLabels[type]}
                            </p>
                            {methodsOfType.map(method => (
                              <div
                                key={method.id}
                                className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                                  method.enabled 
                                    ? "bg-card hover:bg-muted/50" 
                                    : "bg-muted/30 opacity-60"
                                }`}
                                data-testid={`payment-method-${method.id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`p-2 rounded-md ${getPaymentMethodTypeBadge(method.type)}`}>
                                    {getPaymentMethodIcon(method.type, method.gateway_type)}
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">{method.name}</span>
                                      {method.is_default && (
                                        <Badge variant="secondary" className="text-xs">
                                          <Star className="h-3 w-3 mr-1 fill-current" />
                                          Default
                                        </Badge>
                                      )}
                                      {!method.enabled && (
                                        <Badge variant="outline" className="text-xs text-muted-foreground">
                                          Disabled
                                        </Badge>
                                      )}
                                    </div>
                                    {method.type === "bank" && method.bank_name && (
                                      <p className="text-xs text-muted-foreground">
                                        {method.bank_name}
                                        {method.account_number_last4 && ` ****${method.account_number_last4}`}
                                      </p>
                                    )}
                                    {method.type === "gateway" && method.gateway_type && (
                                      <p className="text-xs text-muted-foreground capitalize">
                                        {method.gateway_type}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      className="h-8 w-8"
                                      data-testid={`payment-method-menu-${method.id}`}
                                    >
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => openEditPaymentMethod(method)}>
                                      <Pencil className="h-4 w-4 mr-2" />
                                      Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleTogglePaymentMethod(method.id)}>
                                      <Power className="h-4 w-4 mr-2" />
                                      {method.enabled ? "Disable" : "Enable"}
                                    </DropdownMenuItem>
                                    {!method.is_default && method.enabled && (
                                      <DropdownMenuItem onClick={() => handleSetDefaultPaymentMethod(method.id)}>
                                        <Star className="h-4 w-4 mr-2" />
                                        Set as Default
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem 
                                      onClick={() => handleDeletePaymentMethod(method.id)}
                                      className="text-destructive focus:text-destructive"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Info text */}
                  <div className="p-3 bg-muted/50 rounded-lg text-sm">
                    <p className="text-muted-foreground text-xs">
                      Payment methods are used to track revenue by channel. Add bank accounts, payment gateways, 
                      or other methods to see detailed analytics on where your payments come from.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* AI Memory - User Knowledge Base */}
        <Card data-testid="ai-memory-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
              AI Memory
            </CardTitle>
            <CardDescription>
              Help the AI understand you better by adding personal context and financial objectives
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {isLoadingAIMemory ? (
              <div className="space-y-3">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : (
              <>
                {/* Memories Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Lightbulb className="h-4 w-4 text-amber-500" />
                      <Label className="text-base font-semibold">Memories</Label>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowAddMemory(true)}
                      data-testid="add-memory-button"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add Memory
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Personal context that helps the AI give more relevant advice (e.g., "I prefer conservative investments")
                  </p>
                  
                  {memories.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground border border-dashed rounded-lg">
                      <Lightbulb className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No memories yet. Add some to help the AI understand you better.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {memories.map((memory) => (
                        <div 
                          key={memory.id} 
                          className="flex items-start justify-between p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800"
                          data-testid={`memory-item-${memory.id}`}
                        >
                          <p className="text-sm flex-1 pr-2">{memory.content}</p>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-destructive"
                            onClick={() => handleDeleteMemory(memory.id)}
                            data-testid={`delete-memory-${memory.id}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Objectives Section */}
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-[hsl(var(--ff-primary))]" />
                      <Label className="text-base font-semibold">Financial Objectives</Label>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowAddObjective(true)}
                      data-testid="add-objective-button"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add Objective
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Financial goals for the AI to help you achieve (e.g., "Save $500/month for emergency fund")
                  </p>
                  
                  {objectives.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground border border-dashed rounded-lg">
                      <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No objectives yet. Add financial goals for personalized advice.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {objectives.map((objective) => (
                        <div 
                          key={objective.id} 
                          className="flex items-start justify-between p-3 rounded-lg bg-[hsl(var(--ff-primary)/.05)] border border-[hsl(var(--ff-primary)/.2)]"
                          data-testid={`objective-item-${objective.id}`}
                        >
                          <div className="flex-1 pr-2">
                            <p className="text-sm font-medium">{objective.content}</p>
                            {objective.metadata && (objective.metadata.target_date || objective.metadata.target_amount) && (
                              <div className="flex gap-3 mt-1">
                                {objective.metadata.target_date && (
                                  <span className="text-xs text-muted-foreground">
                                    Target: {new Date(objective.metadata.target_date).toLocaleDateString()}
                                  </span>
                                )}
                                {objective.metadata.target_amount && (
                                  <span className="text-xs text-muted-foreground">
                                    Amount: ${objective.metadata.target_amount.toLocaleString()}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-destructive"
                            onClick={() => handleDeleteObjective(objective.id)}
                            data-testid={`delete-objective-${objective.id}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Exchange Rates - Only show when preferred currency is set */}
        {preferredCurrency && (
          <Card data-testid="exchange-rates-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    {t("exchange_rates", "settings")}
                  </CardTitle>
                  <CardDescription>
                    {t("exchange_rates_desc", "settings")} {preferredCurrency}
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => fetchExchangeRates(preferredCurrency)}
                  disabled={isLoadingRates}
                  data-testid="refresh-rates-button"
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${isLoadingRates ? "animate-spin" : ""}`} />
                  {t("refresh", "common")}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingRates ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : exchangeRates.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  {t("no_exchange_rates", "settings")}
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {exchangeRates.map((rate) => (
                    <div
                      key={rate.target_currency}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{getCurrencySymbol(preferredCurrency)}1</span>
                        <span className="text-muted-foreground">=</span>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-[hsl(var(--ff-primary))]">
                          {getCurrencySymbol(rate.target_currency)}{rate.rate.toFixed(4)}
                        </div>
                        <div className="text-xs text-muted-foreground">{rate.target_currency}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Add Currency Dialog */}
      <Dialog open={showAddCurrency} onOpenChange={setShowAddCurrency}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("add_new_currency", "settings")}</DialogTitle>
            <DialogDescription>
              {t("add_currency_desc", "settings")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t("currency_code", "settings")}</Label>
              <Input
                placeholder="e.g., JPY"
                value={newCurrency.code}
                onChange={(e) => setNewCurrency({ ...newCurrency, code: e.target.value.toUpperCase() })}
                maxLength={3}
                data-testid="new-currency-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("currency_name", "settings")}</Label>
              <Input
                placeholder="e.g., Japanese Yen"
                value={newCurrency.name}
                onChange={(e) => setNewCurrency({ ...newCurrency, name: e.target.value })}
                data-testid="new-currency-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("symbol", "settings")}</Label>
              <Input
                placeholder="e.g., ¥"
                value={newCurrency.symbol}
                onChange={(e) => setNewCurrency({ ...newCurrency, symbol: e.target.value })}
                data-testid="new-currency-symbol-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCurrency(false)}>{t("cancel", "common")}</Button>
            <Button onClick={handleAddCurrency} disabled={isAddingCurrency} data-testid="confirm-add-currency-button">
              {isAddingCurrency ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t("add_currency", "settings")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Memory Dialog */}
      <Dialog open={showAddMemory} onOpenChange={setShowAddMemory}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              Add Memory
            </DialogTitle>
            <DialogDescription>
              Add personal context to help the AI give more relevant financial advice
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Memory</Label>
              <Textarea
                placeholder="e.g., I prefer conservative investments, My salary comes on the 15th of each month, I'm saving for a house down payment..."
                value={newMemory}
                onChange={(e) => setNewMemory(e.target.value)}
                rows={4}
                data-testid="new-memory-input"
              />
              <p className="text-xs text-muted-foreground">
                This information will be used by the AI to provide personalized advice
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMemory(false)}>Cancel</Button>
            <Button onClick={handleAddMemory} disabled={isAddingMemory} data-testid="confirm-add-memory-button">
              {isAddingMemory ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Add Memory
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Objective Dialog */}
      <Dialog open={showAddObjective} onOpenChange={setShowAddObjective}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
              Add Financial Objective
            </DialogTitle>
            <DialogDescription>
              Define a financial goal for the AI to help you achieve
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Objective</Label>
              <Textarea
                placeholder="e.g., Save $500/month for emergency fund, Reduce dining expenses by 30%, Pay off credit card debt..."
                value={newObjective.content}
                onChange={(e) => setNewObjective({ ...newObjective, content: e.target.value })}
                rows={3}
                data-testid="new-objective-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Target Date (Optional)</Label>
                <Input
                  type="date"
                  value={newObjective.target_date}
                  onChange={(e) => setNewObjective({ ...newObjective, target_date: e.target.value })}
                  data-testid="objective-target-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Target Amount (Optional)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 5000"
                  value={newObjective.target_amount}
                  onChange={(e) => setNewObjective({ ...newObjective, target_amount: e.target.value })}
                  data-testid="objective-target-amount-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddObjective(false)}>Cancel</Button>
            <Button onClick={handleAddObjective} disabled={isAddingObjective} data-testid="confirm-add-objective-button">
              {isAddingObjective ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Add Objective
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Payment Method Dialog */}
      <Dialog open={showPaymentMethodDialog} onOpenChange={setShowPaymentMethodDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
              {editingPaymentMethod ? (t("edit_payment_method", "company") || "Edit Payment Method") : (t("add_payment_method", "company") || "Add Payment Method")}
            </DialogTitle>
            <DialogDescription>
              {editingPaymentMethod 
                ? "Update the payment method details" 
                : "Add a new payment method for tracking revenue"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Payment Method Type */}
            <div className="space-y-2">
              <Label>Type *</Label>
              <Select 
                value={paymentMethodForm.type} 
                onValueChange={(value) => setPaymentMethodForm({ ...paymentMethodForm, type: value, gateway_type: "" })}
              >
                <SelectTrigger data-testid="payment-method-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gateway">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Payment Gateway (Stripe, PayPal, etc.)
                    </div>
                  </SelectItem>
                  <SelectItem value="bank">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Bank Account
                    </div>
                  </SelectItem>
                  <SelectItem value="cash">
                    <div className="flex items-center gap-2">
                      <Banknote className="h-4 w-4" />
                      Cash
                    </div>
                  </SelectItem>
                  <SelectItem value="check">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Check
                    </div>
                  </SelectItem>
                  <SelectItem value="other">
                    <div className="flex items-center gap-2">
                      <Wallet className="h-4 w-4" />
                      Other
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder={
                  paymentMethodForm.type === "gateway" ? "e.g., Stripe, PayPal" :
                  paymentMethodForm.type === "bank" ? "e.g., Mercury Business, Chase Checking" :
                  "e.g., Cash, Company Check"
                }
                value={paymentMethodForm.name}
                onChange={(e) => setPaymentMethodForm({ ...paymentMethodForm, name: e.target.value })}
                data-testid="payment-method-name-input"
              />
            </div>

            {/* Gateway-specific fields */}
            {paymentMethodForm.type === "gateway" && (
              <div className="space-y-2">
                <Label>Gateway Provider</Label>
                <Select 
                  value={paymentMethodForm.gateway_type} 
                  onValueChange={(value) => setPaymentMethodForm({ ...paymentMethodForm, gateway_type: value })}
                >
                  <SelectTrigger data-testid="gateway-type-select">
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stripe">Stripe</SelectItem>
                    <SelectItem value="paypal">PayPal</SelectItem>
                    <SelectItem value="square">Square</SelectItem>
                    <SelectItem value="venmo">Venmo</SelectItem>
                    <SelectItem value="card">Credit Card (Generic)</SelectItem>
                    <SelectItem value="other">Other Gateway</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Bank-specific fields */}
            {paymentMethodForm.type === "bank" && (
              <>
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <Input
                    placeholder="e.g., Mercury, Chase, Bank of America"
                    value={paymentMethodForm.bank_name}
                    onChange={(e) => setPaymentMethodForm({ ...paymentMethodForm, bank_name: e.target.value })}
                    data-testid="bank-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Account Name</Label>
                    <Input
                      placeholder="e.g., Business Checking"
                      value={paymentMethodForm.account_name}
                      onChange={(e) => setPaymentMethodForm({ ...paymentMethodForm, account_name: e.target.value })}
                      data-testid="account-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last 4 Digits</Label>
                    <Input
                      placeholder="e.g., 4521"
                      value={paymentMethodForm.account_number_last4}
                      onChange={(e) => setPaymentMethodForm({ ...paymentMethodForm, account_number_last4: e.target.value.slice(0, 4) })}
                      maxLength={4}
                      data-testid="account-last4-input"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Color selector */}
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {["gray", "red", "orange", "amber", "green", "emerald", "teal", "blue", "indigo", "purple", "pink"].map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setPaymentMethodForm({ ...paymentMethodForm, color })}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      paymentMethodForm.color === color 
                        ? "border-foreground scale-110" 
                        : "border-transparent hover:scale-105"
                    }`}
                    style={{
                      backgroundColor: color === "gray" ? "#6b7280" :
                        color === "red" ? "#ef4444" :
                        color === "orange" ? "#f97316" :
                        color === "amber" ? "#f59e0b" :
                        color === "green" ? "#22c55e" :
                        color === "emerald" ? "#10b981" :
                        color === "teal" ? "#14b8a6" :
                        color === "blue" ? "#3b82f6" :
                        color === "indigo" ? "#6366f1" :
                        color === "purple" ? "#a855f7" :
                        color === "pink" ? "#ec4899" : "#6b7280"
                    }}
                    data-testid={`color-${color}`}
                  />
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Input
                placeholder="Any additional notes..."
                value={paymentMethodForm.notes}
                onChange={(e) => setPaymentMethodForm({ ...paymentMethodForm, notes: e.target.value })}
                data-testid="payment-method-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentMethodDialog(false)}>Cancel</Button>
            <Button onClick={handleSavePaymentMethod} disabled={isSavingPaymentMethod} data-testid="save-payment-method-button">
              {isSavingPaymentMethod ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              {editingPaymentMethod ? "Update" : "Add"} Payment Method
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
